import { useRef, useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

import styles from './Modal.module.css';

export const Modal: React.FC<ModalProps> = (props: ModalProps) => {
	const modalRef = useRef<HTMLDialogElement | null>(null);
	const { isOpen, hasCloseBtn, onClose, children } = props;
	const classNames = [styles['modal']];
	const [isModalOpen, setModalOpen] = useState(isOpen);
	if (props?.classNames) {
		for (const c of props.classNames) {
			classNames.push(c);
		}
	}
	const handleCloseModal = useCallback(() => {
		if (onClose) {
			onClose();
		}

		setModalOpen(false);
	}, [onClose]);
	useEffect(() => {
		setModalOpen(isOpen);
	}, [isOpen]);
	useEffect(() => {
		if (modalRef === null) {
			return;
		}
		const dialog = modalRef.current;
		if (!dialog) {
			return;
		}

		dialog.addEventListener('click', evt => {
			const rect = dialog.getBoundingClientRect();
			const isInDialog =
				rect.top <= evt.clientY &&
				evt.clientY <= rect.top + rect.height &&
				rect.left <= evt.clientX &&
				evt.clientX <= rect.left + rect.width;
			if (!isInDialog) {
				dialog.close();
				handleCloseModal();
			}
		});
	}, [modalRef, handleCloseModal]);
	useEffect(() => {
		if (modalRef === null) {
			return;
		}
		const dialog = modalRef.current;
		if (!dialog) {
			return;
		}

		if (isModalOpen) {
			dialog.showModal();
		} else {
			dialog.close();
		}
	}, [isModalOpen, modalRef]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDialogElement>) => {
		if (event.key === 'Escape') {
			handleCloseModal();
		}
	};

	return (
		<dialog
			ref={modalRef}
			className={classNames.join(' ')}
			onKeyDown={handleKeyDown}
		>
			{hasCloseBtn && (
				<button
					type="button"
					title="Close"
					className={styles['modal-close-btn']}
					onClick={handleCloseModal}
				>
					<X color={'#c3c3c3'} />
				</button>
			)}
			{children}
		</dialog>
	);
};

interface ModalProps {
	isOpen: boolean;
	hasCloseBtn?: boolean;
	onClose?: () => void;
	children: React.ReactNode;
	classNames?: Array<string>;
}
